/**
 * useIdleLogout — session continuation (idle warning) manager.
 *
 *   - Tracks browser activity (mousemove / keydown / click / scroll / touch).
 *   - After IDLE_MS of inactivity, shows a session-continuation WARNING MODAL
 *     with a COUNTDOWN_S-second countdown instead of logging out silently.
 *       • "Continue Session" → resets the idle timer and closes the modal.
 *       • "Log Out" or the countdown reaching zero → logs out and redirects
 *         to /login?session=expired.
 *   - On window/tab close (pagehide with persisted=false), fires a keep-alive
 *     logout beacon so the next browser session starts clean.
 *   - Skipped when the user is not authenticated.
 *   - Cross-tab aware: activity (or "Continue") in any tab resets the others.
 *
 * Returns { warningOpen, secondsLeft, continueSession, logoutNow } so the
 * mounting component can render the modal.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const IDLE_MS = 10 * 60 * 1000; // 10 minutes of inactivity before the warning
const COUNTDOWN_S = 60;          // seconds shown in the modal before auto-logout
const STORAGE_KEY = 'cb_last_activity_at';
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'click', 'scroll', 'touchstart', 'wheel'];
const REDIRECT_PATH = '/login?session=expired';
const API_URL = process.env.REACT_APP_BACKEND_URL;

export const useIdleLogout = ({ user, logout }) => {
  const navigate = useNavigate();
  const [warningOpen, setWarningOpen] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_S);
  const [countdownTotal, setCountdownTotal] = useState(COUNTDOWN_S);

  const idleTimerRef = useRef(null);
  const countdownRef = useRef(null);
  const secondsRef = useRef(COUNTDOWN_S);
  const warningOpenRef = useRef(false);
  const logoutRef = useRef(null);
  const navigateRef = useRef(null);
  // Button handlers live in refs so the returned callbacks stay stable while
  // the main effect re-runs only when auth state changes.
  const continueRef = useRef(() => {});
  const logoutNowRef = useRef(() => {});

  useEffect(() => { logoutRef.current = logout; });
  useEffect(() => { navigateRef.current = navigate; });

  const isAuth = !!user;

  useEffect(() => {
    if (!isAuth) {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
      warningOpenRef.current = false;
      setWarningOpen(false);
      return undefined;
    }

    const clearCountdown = () => {
      if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
    };
    const clearIdle = () => {
      if (idleTimerRef.current) { clearTimeout(idleTimerRef.current); idleTimerRef.current = null; }
    };

    // Optional overrides (used by automated tests to avoid a real 10-min wait).
    // Read FRESH each schedule so they can be toggled at runtime. Never set
    // during normal use → production uses IDLE_MS / COUNTDOWN_S.
    const readNum = (k) => {
      try { const v = Number(localStorage.getItem(k)); return Number.isFinite(v) && v > 0 ? v : null; }
      catch (_) { return null; }
    };
    const getIdleMs = () => readNum('cb_idle_ms_override') || IDLE_MS;
    const getCountdownS = () => readNum('cb_idle_countdown_override') || COUNTDOWN_S;

    const doLogout = () => {
      clearIdle();
      clearCountdown();
      warningOpenRef.current = false;
      setWarningOpen(false);
      Promise.resolve(logoutRef.current?.()).catch(() => {});
      try { localStorage.removeItem(STORAGE_KEY); } catch (_) { /* ignore */ }
      navigateRef.current?.(REDIRECT_PATH);
    };

    const showWarning = () => {
      clearIdle();
      const cs = getCountdownS();
      warningOpenRef.current = true;
      setWarningOpen(true);
      secondsRef.current = cs;
      setSecondsLeft(cs);
      setCountdownTotal(cs);
      clearCountdown();
      countdownRef.current = setInterval(() => {
        const next = secondsRef.current - 1;
        secondsRef.current = next;
        if (next <= 0) {
          clearCountdown();
          setSecondsLeft(0);
          doLogout();
        } else {
          setSecondsLeft(next);
        }
      }, 1000);
    };

    const scheduleIdle = (ms) => {
      clearIdle();
      const delay = ms == null ? getIdleMs() : ms;
      idleTimerRef.current = setTimeout(showWarning, Math.max(0, delay));
    };

    const recordActivityAndReset = () => {
      const now = Date.now();
      try { localStorage.setItem(STORAGE_KEY, String(now)); } catch (_) { /* ignore */ }
      scheduleIdle();
    };

    const continueSession = () => {
      clearCountdown();
      warningOpenRef.current = false;
      setWarningOpen(false);
      recordActivityAndReset();
    };

    continueRef.current = continueSession;
    logoutNowRef.current = doLogout;

    // While the warning is open we IGNORE passive activity — the user must
    // explicitly click "Continue Session". Otherwise a stray mousemove would
    // dismiss the modal silently.
    const onActivity = () => {
      if (warningOpenRef.current) return;
      recordActivityAndReset();
    };
    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, onActivity, { passive: true }));

    // Cross-tab sync — newer activity (or a "Continue") in another tab resets
    // this tab's timer and dismisses any open warning here.
    const onStorage = (e) => {
      if (e.key !== STORAGE_KEY || !e.newValue) return;
      const ts = parseInt(e.newValue, 10);
      if (!Number.isFinite(ts)) return;
      if (warningOpenRef.current) {
        clearCountdown();
        warningOpenRef.current = false;
        setWarningOpen(false);
      }
      scheduleIdle(getIdleMs() - (Date.now() - ts));
    };
    window.addEventListener('storage', onStorage);

    // Tab/window close — fire logout via sendBeacon (keeps the next session clean).
    const onPageHide = (e) => {
      if (e.persisted) return;
      try {
        const url = `${API_URL}/api/auth/logout`;
        const blob = new Blob([JSON.stringify({})], { type: 'application/json' });
        const ok = navigator.sendBeacon && navigator.sendBeacon(url, blob);
        if (!ok) {
          fetch(url, {
            method: 'POST', credentials: 'include', keepalive: true,
            headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}),
          }).catch(() => {});
        }
        try { localStorage.setItem('cb_tab_closed_logout', '1'); } catch (_) { /* ignore */ }
      } catch (_) { /* never block unload */ }
    };
    window.addEventListener('pagehide', onPageHide);

    // Start the clock
    recordActivityAndReset();

    return () => {
      clearIdle();
      clearCountdown();
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, onActivity));
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, [isAuth]);

  const continueSession = useCallback(() => continueRef.current?.(), []);
  const logoutNow = useCallback(() => logoutNowRef.current?.(), []);

  return { warningOpen, secondsLeft, countdownTotal, continueSession, logoutNow };
};

export default useIdleLogout;
